import * as React from "react";
import Select from "@/components/Select";
import IconTrash1 from "@/icons/IconTrash1";
import { useMemo } from "react";
import {
  FormValidator,
  QuestionType,
  questionTypeDefs,
  SurveyQuestion
} from "@velaro/velaro-component-lib";
import useSubscriptions from "@/hooks/useSubscriptions";
import { languageOptionsList } from "@/constants/languages";
import ReactSelect from "react-select";

interface Props {
  question: SurveyQuestion;
  errors: Record<string, string>;
  netSuiteMappings?: any[];
  loadingMappings?: boolean;
  update(question: SurveyQuestion): void;
  remove(): void;
}

//some question types will have hard-cded variables associated with them
function hasFixedVariable(type: QuestionType | string) {
  switch (type) {
    case "name":
    case "email":
    case "crmField":
    case "language":
      return true;
    default:
      return false;
  }
}

export default function QuestionEditor({
  question,
  errors,
  netSuiteMappings = [],
  loadingMappings = false,
  update,
  remove
}: Props) {
  const variableFixed = hasFixedVariable(question.type);
  const { subscription } = useSubscriptions();

  //dont show crmField option if Netsuite is not enabled since the user will have nothing to map to
  const availableQuestionTypes = useMemo(() => {
    const allOptions = Object.entries(questionTypeDefs).map(
      ([value, label]) => ({
        value,
        label
      })
    );

    if (!subscription?.enableNetsuite) {
      return allOptions.filter((option) => option.value !== "crmField");
    }
    return allOptions;
  }, [subscription?.enableNetsuite]);

  const netSuiteOptions = useMemo(() => {
    if (loadingMappings) {
      return [{ label: "Loading...", value: "" }];
    }

    if (!netSuiteMappings || netSuiteMappings.length === 0) {
      return [{ label: "No NetSuite fields available", value: "" }];
    }

    return [
      ...netSuiteMappings.map((mapping) => ({
        label: `${mapping.recordType} - ${mapping.netSuiteField}`,
        value: mapping.netSuiteField,
        recordType: mapping.recordType,
        netSuiteField: mapping.netSuiteField
      }))
    ];
  }, [netSuiteMappings, loadingMappings]);

  let fixedVariableName = null;
  if (variableFixed) {
    fixedVariableName = question.variable;
  }

  function changeType(type: QuestionType | string) {
    const wasFixed = variableFixed;
    const isFixed = hasFixedVariable(type);

    const questionType = type as QuestionType;

    if (type === "language") {
      update({
        ...question,
        type: questionType,
        variable: "language",
        options: []
      });
      return;
    }

    if (isFixed) {
      //force variable for this question type
      update({ ...question, type: questionType, variable: type });
      return;
    }

    if (wasFixed) {
      //clear variable if switching off of forced variable type
      update({ ...question, type: questionType, variable: "" });
      return;
    }

    //otherwise leave variable alone
    update({ ...question, type: questionType });
  }

  const optionsList = useMemo(
    () => (question.options ? question.options.join("\n") : ""),
    [question.options]
  );

  const typeHasOptions =
    question.type === "choice" ||
    question.type === "checkbox" ||
    question.type === "crmField" ||
    (question.type as string) === "language";

  const typeHasInput = question.type !== "label";
  return (
    <tr>
      <td>
        <FormValidator error={errors["text"]} errorClassName="absolute">
          <input
            type="text"
            className="input w-full"
            required
            value={question.text}
            onChange={(e) =>
              update({ ...question, text: e.currentTarget.value })
            }
          />
        </FormValidator>
      </td>
      <td>
        <div className="flex flex-col">
          <Select
            options={availableQuestionTypes}
            className="input w-full"
            value={question.type || "text"}
            onChange={changeType}
          />
          {typeHasOptions &&
            (question.type === "crmField" ? (
              <Select
                options={netSuiteOptions}
                className="input w-full"
                value={question.variable || ""}
                onChange={(value) => {
                  // Find the selected mapping to get recordType
                  const selectedOption = netSuiteOptions.find(
                    (opt) => opt.value === value
                  );
                  if (
                    selectedOption &&
                    "recordType" in selectedOption &&
                    selectedOption.recordType
                  ) {
                    update({
                      ...question,
                      variable: value,
                      crmOptions: {
                        recordType: selectedOption.recordType,
                        netSuiteField: selectedOption.netSuiteField
                      }
                    });
                  } else {
                    update({
                      ...question,
                      variable: value,
                      crmOptions: undefined
                    });
                  }
                }}
              />
            ) : (question.type as string) === "language" ? (
              <>
                <ReactSelect
                  isMulti
                  name="languages"
                  options={languageOptionsList.map((lang) => ({
                    value: lang,
                    label: lang
                  }))}
                  value={(question.options || [])
                    .filter((o) => o.trim())
                    .map((lang) => ({ value: lang, label: lang }))}
                  className="basic-multi-select w-full mt-2"
                  classNamePrefix="select"
                  placeholder="Select languages (blank = all)..."
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "40px"
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      flexWrap: "wrap",
                      maxHeight: "120px",
                      overflowY: "auto"
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    })
                  }}
                  onChange={(selected) => {
                    const val = selected
                      ? selected.map((item) => item.value)
                      : [];
                    update({ ...question, options: val });
                  }}
                />
                <div className="mt-1 text-xs text-gray-500">
                  {question.options && question.options.length > 0
                    ? `${question.options.filter((o) => o.trim()).length} languages selected`
                    : `Blank = all ${languageOptionsList.length} languages`}
                </div>
              </>
            ) : (
              <textarea
                className="input mt-2"
                placeholder={`choice 1\nchoice 2\nchoice 3`}
                value={optionsList}
                onChange={(e) => {
                  //split by new line and convert to record
                  const val = e.currentTarget.value.split("\n");
                  update({ ...question, options: val });
                }}
              />
            ))}
        </div>
      </td>
      <td>
        {typeHasInput && (
          <input
            disabled={variableFixed}
            type="text"
            className="input w-full"
            value={fixedVariableName || question.variable || ""}
            onChange={(e) =>
              update({ ...question, variable: e.currentTarget.value })
            }
          />
        )}
      </td>
      <td>
        {typeHasInput && (
          <input
            type="checkbox"
            className="input w-full"
            checked={question.required}
            onChange={() =>
              update({ ...question, required: !question.required })
            }
          />
        )}
      </td>
      <td>
        <button className="cursor-pointer" onClick={remove}>
          <IconTrash1 />
        </button>
      </td>
    </tr>
  );
}
